import {
  DatePicker as AntDatePicker,
  DatePickerProps as AntDatePickerProps,
} from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import localeData from "dayjs/plugin/localeData";
import minMax from "dayjs/plugin/minMax";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { defineMessages, useIntl } from "react-intl";

import { TIME_FORMAT } from "../../lib/constants";
import {
  WeekdaysShort,
  formatWeek,
  type DateTimeType,
} from "../../lib/formats/dates";
import type {
  CropCycleShortFragment,
  LocalityNameFragment,
} from "../../lib/graphql";
import { useCurrentLocale } from "../../lib/hooks/basic/locales";
import { useWeekDaysAndHolidays } from "../../lib/hooks/hr/holidays";
import { Form, type NamePath, shouldUpdate } from "./Form";

dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekday);
dayjs.extend(isBetween);
dayjs.extend(minMax);

// TODO(antd-v6): generatePicker was removed in antd v6 (now dayjs-native by default).
// Previously we customized getNow: () => dayjs().tz() so the picker's "today"
// respected dayjs.tz default zone. With direct DatePicker import, "now" reverts
// to local-system time. parseWithTimezone(...) on values keeps user-facing
// dates timezone-correct; revisit if QA flags the regression.
export { AntDatePicker };

export type { RangePickerProps };

type BaseDatePickerProps = Omit<AntDatePickerProps, "picker" | "onChange"> & {
  showWeeks?:
    | boolean
    | Pick<CropCycleShortFragment, "startedAt" | "estimatedEndDate">
    | null;
  disableDates?: boolean | DateTimeType;
  popupClassName?: string;
  defaultTime?: "startOfDay";
  locality?: LocalityNameFragment;
};

export type DatePickerProps = BaseDatePickerProps &
  (
    | {
        asStringDate: true;
        onChange?: (value: string | null, dateString: string) => void;
      }
    | {
        asStringDate?: false;
        onChange?: (value: Dayjs | null, dateString: string) => void;
      }
  );

const DATE_FORMAT = "DD/MM/YYYY";
const MONTH_FORMAT = "MM/YYYY";

const messages = defineMessages({
  selectDate: {
    id: "selectDate",
    defaultMessage: "Select Date",
  },
  selectMonth: {
    id: "selectMonth",
    defaultMessage: "Select Month",
  },
  selectYear: {
    id: "selectYear",
    defaultMessage: "Select Year",
  },
  selectWeek: {
    id: "selectWeek",
    defaultMessage: "Select Week",
  },
  afterEstimatedDate: {
    id: "date.afterCropCycleEndDateWarning",
    defaultMessage: "Date is after crop cycle estimation end date ({date})",
  },
});

function withPopupRootClassName<
  T extends BaseDatePickerProps["classNames"] | RangePickerProps["classNames"],
>(
  pickerClassNames: T | undefined,
  ...popupClassNames: classNames.ArgumentArray
): T {
  if (typeof pickerClassNames === "function") return pickerClassNames;

  const popup = pickerClassNames?.popup;

  return {
    ...pickerClassNames,
    popup: {
      ...(typeof popup === "object" ? popup : undefined),
      root: classNames(
        typeof popup === "string" ? popup : popup?.root,
        popupClassNames
      ),
    },
  } as T;
}

// dayjs("2023-03-13T22:00:00-06:00") - create dayjs applying browser timezone (+9 hours MSK)
// dayjs("2023-03-13T22:00:00-06:00").tz() - convert to default timezone (-9 hours MSK)
// dayjs.tz("2023-03-13T22:00:00-06:00") - parse with zone offset (+6 hours MSK)

// dayjs("2023-03-13") - create dayjs without applying browser timezone (+0 hours)
// dayjs("2023-03-13").tz() - convert to default timezone (-9 hours MSK) (2023-03-12 15:00)
// dayjs.tz("2023-03-13") - parse with zone offset (+0 hours MSK)

// dayjs("2023-04-01T20:00:00.095Z") - create dayjs applying browser timezone (+3 hours MSK)
// dayjs("2023-04-01T20:00:00.095Z").tz() - convert to default timezone (-6 hours MSK)
// dayjs.tz("2023-04-01T20:00:00.095Z") - parse with zone offset (+0 hours MSK)
function parseWithTimezone(value?: dayjs.Dayjs | null | string) {
  if (!value) return undefined;

  if (dayjs(value, "YYYY-MM-DD", true).isValid()) return dayjs.tz(value);

  return dayjs(value).tz();
}

function HolidayDate({
  date,
  warning,
  locality,
}: {
  date: Dayjs;
  warning?: string;
  locality?: LocalityNameFragment;
}) {
  const { holidays, weekDays } = useWeekDaysAndHolidays(date.year());

  const weekDay = WeekdaysShort[date.day()];
  const nonWorking = weekDays?.some((d) => d.name == weekDay && !d.working);
  const holiday = holidays?.find(
    (h) =>
      date.isSame(dayjs.tz(h.overrideDate || h.date), "day") &&
      (!locality ||
        h.localities.length === 0 ||
        h.localities.some((l) => l.id == locality.id))
  );

  const title = holiday
    ? locality && holiday.localities.length
      ? `${holiday.description} - ${locality.name}`
      : String(holiday.description)
    : warning;

  return (
    <div
      className={classNames(
        "ant-picker-cell-inner",
        nonWorking ? "non-working-date" : undefined,
        holiday ? "holiday-date" : undefined,
        warning ? "warning-date" : undefined
      )}
      title={title}
    >
      {date.date()}
    </div>
  );
}

export const DatePicker = ({
  value,
  showWeeks,
  disableDates,
  popupClassName,
  classNames: pickerClassNames,
  defaultTime,
  locality,
  onChange,
  asStringDate,
  ...props
}: DatePickerProps) => {
  const intl = useIntl();
  const { antLocale } = useCurrentLocale();
  const firstDay = dayjs.localeData().firstDayOfWeek();

  const handleChange = (value: Dayjs | null, dateString: string) => {
    if (!onChange) return;

    if (asStringDate) {
      onChange(value ? value.format("YYYY-MM-DD") : null, dateString);
    } else {
      onChange(
        defaultTime == "startOfDay" && value ? value.startOf("day") : value,
        dateString
      );
    }
  };

  return (
    <AntDatePicker
      classNames={withPopupRootClassName(
        pickerClassNames,
        popupClassName,
        showWeeks ? "week-panel" : undefined,
        "notranslate"
      )}
      locale={antLocale.DatePicker}
      cellRender={
        showWeeks
          ? (current, info) => {
              if (
                info.type !== "date" ||
                typeof current === "string" ||
                typeof current === "number"
              ) {
                return info.originNode;
              }
              const date = current;
              return (
                <div className="ant-picker-cell-inner">
                  {date.day() == firstDay && (
                    <div
                      className="week-number"
                      title={intl.formatMessage(
                        { id: "weekNumber" },
                        { week: date.week() }
                      )}
                    >
                      {showWeeks === true
                        ? date.week()
                        : formatWeek(
                            date,
                            showWeeks.startedAt,
                            showWeeks.estimatedEndDate
                          )}
                    </div>
                  )}
                  <HolidayDate
                    date={date}
                    locality={locality}
                    warning={
                      showWeeks !== true &&
                      showWeeks.estimatedEndDate &&
                      date.isAfter(showWeeks.estimatedEndDate)
                        ? intl.formatMessage(messages.afterEstimatedDate, {
                            date: intl.formatDate(showWeeks.estimatedEndDate),
                          })
                        : undefined
                    }
                  />
                </div>
              );
            }
          : (current, info) => {
              if (
                info.type !== "date" ||
                typeof current === "string" ||
                typeof current === "number"
              ) {
                return info.originNode;
              }
              return <HolidayDate date={current} locality={locality} />;
            }
      }
      format={DATE_FORMAT}
      value={parseWithTimezone(value as Dayjs | null | string | undefined)}
      placeholder={intl.formatMessage(messages.selectDate)}
      style={{ width: "100%" }}
      disabledDate={
        disableDates && showWeeks && showWeeks !== true
          ? (date) => {
              const startDate =
                disableDates === true
                  ? dayjs(showWeeks.startedAt).startOf("day")
                  : dayjs(disableDates);

              return dayjs(date).isBefore(startDate);
            }
          : undefined
      }
      onChange={handleChange as AntDatePickerProps["onChange"]}
      {...props}
      defaultPickerValue={
        typeof props.defaultPickerValue === "string"
          ? parseWithTimezone(props.defaultPickerValue)
          : props.defaultPickerValue
      }
    />
  );
};

export type RangePickerValue = [Dayjs | null, Dayjs | null] | null;

export function isRangeType(rangeType: any) {
  return rangeType === "range" || rangeType === "monthRange";
}

type RangePickerInputProps = (
  | (RangePickerProps & { asStringDate?: false })
  | (Omit<RangePickerProps, "value" | "onChange"> & {
      asStringDate: true;
      value?: RangePickerProps["value"] | [string | null, string | null];
      onChange?(
        value: [string | null, string | null] | null,
        dateStrings: [string, string]
      ): void;
    })
) & {
  popupClassName?: string;
  locality?: LocalityNameFragment;
};

export const RangePicker = ({
  value,
  onChange,
  asStringDate,
  popupClassName,
  classNames: pickerClassNames,
  locality,
  ...props
}: RangePickerInputProps) => {
  const { antLocale } = useCurrentLocale();

  return (
    <AntDatePicker.RangePicker
      onChange={(range, dateStrings) => {
        if (asStringDate) {
          onChange?.(
            range
              ? [
                  range[0]?.format("YYYY-MM-DD") ?? null,
                  range[1]?.format("YYYY-MM-DD") ?? null,
                ]
              : null,
            dateStrings
          );
        } else {
          onChange?.(range, dateStrings);
        }
      }}
      locale={antLocale.DatePicker}
      style={{ width: "100%" }}
      format={props.picker === "month" ? MONTH_FORMAT : DATE_FORMAT}
      value={
        value
          ? [
              parseWithTimezone(value[0]) || null,
              parseWithTimezone(value[1]) || null,
            ]
          : undefined
      }
      classNames={withPopupRootClassName(
        pickerClassNames,
        popupClassName,
        "notranslate"
      )}
      cellRender={(current, info) => {
        if (
          info.type !== "date" ||
          typeof current === "string" ||
          typeof current === "number"
        ) {
          return info.originNode;
        }
        return <HolidayDate date={current} locality={locality} />;
      }}
      {...props}
    />
  );
};

export type RangePickerFormItemProps = {
  startName: NamePath;
  endName: NamePath;
  defaultDate?: any;
  value?: any;
  asStringDate?: boolean;
} & RangePickerProps;

function RangePickerFormItem({
  startName,
  endName,
  defaultDate,
  value,
  onChange,
  asStringDate,
  ...props
}: RangePickerFormItemProps) {
  return (
    <Form.Item noStyle shouldUpdate={shouldUpdate(startName, endName)}>
      {({ getFieldValue, setFields }) => {
        return (
          <DatePicker.RangePicker
            defaultPickerValue={
              getFieldValue(startName)
                ? dayjs(getFieldValue(startName))
                : dayjs(defaultDate)
            }
            value={
              getFieldValue(startName) && [
                getFieldValue(startName),
                getFieldValue(endName),
              ]
            }
            onChange={(values, stringDates) => {
              if (onChange) onChange(values, stringDates);

              const startValue = asStringDate
                ? values?.[0]?.format("YYYY-MM-DD")
                : values?.[0]?.startOf("day");
              const endValue = asStringDate
                ? values?.[1]?.format("YYYY-MM-DD")
                : values?.[1]?.endOf("day");

              setFields([
                { name: startName, value: startValue },
                { name: endName, value: endValue },
              ]);
            }}
            {...props}
          />
        );
      }}
    </Form.Item>
  );
}

DatePicker.RangePicker = RangePicker;
DatePicker.RangePickerFormItem = RangePickerFormItem;

const YearPicker = ({
  value,
  onChange,
  popupClassName,
  classNames: pickerClassNames,
  ...props
}: Omit<DatePickerProps, "onChange"> & {
  onChange?(value: number | null): void;
}) => {
  const intl = useIntl();

  return (
    <AntDatePicker
      autoComplete="no"
      allowClear
      placeholder={intl.formatMessage(messages.selectYear)}
      value={value ? dayjs.tz(value.toString()) : undefined}
      picker="year"
      onChange={(value) => {
        const single = Array.isArray(value) ? value[0] : value;
        return (
          onChange &&
          onChange(single ? Number(dayjs(single).format("YYYY")) : null)
        );
      }}
      classNames={withPopupRootClassName(
        pickerClassNames,
        popupClassName,
        "notranslate"
      )}
      {...props}
    />
  );
};

DatePicker.YearPicker = YearPicker;

export const DateTimePicker = (props: DatePickerProps) => (
  <DatePicker showTime format={`${DATE_FORMAT} ${TIME_FORMAT}`} {...props} />
);
